import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { chatAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Bot, User, Loader2, X, Sparkles, Maximize2, Minimize2
} from 'lucide-react';

function MiniMarkdown({ text }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-0.5 text-[13px] leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith('### ')) return <h4 key={i} className="font-semibold mt-2 mb-0.5">{line.slice(4)}</h4>;
        if (line.startsWith('## ')) return <h3 key={i} className="font-semibold mt-2 mb-0.5">{line.slice(3)}</h3>;
        if (line.match(/^[-*]\s/)) {
          return <li key={i} className="ml-3 list-disc"><InlineFmt text={line.slice(2)} /></li>;
        }
        if (line.match(/^\d+\.\s/)) {
          return <li key={i} className="ml-3 list-decimal"><InlineFmt text={line.replace(/^\d+\.\s/, '')} /></li>;
        }
        if (line.trim() === '') return <div key={i} className="h-1" />;
        return <p key={i}><InlineFmt text={line} /></p>;
      })}
    </div>
  );
}

function InlineFmt({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={i} className="bg-black/10 px-1 py-0.5 rounded text-xs font-mono">{p.slice(1, -1)}</code>;
    return <span key={i}>{p}</span>;
  });
}

export default function FloatingChat() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 200); }, [open]);

  // Hide on AI Assistant page and public pages
  const hiddenPaths = ['/ai-assistant', '/client-portal', '/login', '/forgot-password', '/reset-password', '/', '/pricing', '/faq', '/collect'];
  if (!user || hiddenPaths.some(p => location.pathname === p || location.pathname.startsWith('/collect/'))) return null;

  const initSession = async () => {
    if (sessionId) return;
    setLoading(true);
    try {
      // Try to reuse the most recent session
      const res = await chatAPI.listSessions();
      const sessions = res.data || [];
      if (sessions.length > 0) {
        const latest = sessions[0];
        setSessionId(latest.id);
        const sessionRes = await chatAPI.getSession(latest.id);
        setMessages(sessionRes.data?.messages || []);
      } else {
        const newRes = await chatAPI.createSession({ title: 'Quick Chat' });
        setSessionId(newRes.data.id);
        setMessages([]);
      }
    } catch {
      toast.error('Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setUnread(0);
    initSession();
  };

  const startNewChat = async () => {
    setLoading(true);
    try {
      const res = await chatAPI.createSession({ title: 'Quick Chat' });
      setSessionId(res.data.id);
      setMessages([]);
    } catch {
      toast.error('Failed to start new chat');
    } finally {
      setLoading(false);
    }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending || !sessionId) return;

    const userMsg = message.trim();
    setMessage('');
    setSending(true);

    const tempMsg = { role: 'user', content: userMsg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const res = await chatAPI.sendMessage(sessionId, { message: userMsg });
      setMessages(prev => [
        ...prev.slice(0, -1),
        res.data.user_message,
        res.data.ai_message
      ]);
    } catch (err) {
      setMessages(prev => prev.slice(0, -1));
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const quickQuestions = [
    'What USCIS forms do I need?',
    'Show my active cases',
    'How to file I-485?',
    'Document checklist for work visa',
  ];

  const panelWidth = expanded ? 'w-[560px]' : 'w-[380px]';
  const panelHeight = expanded ? 'h-[600px]' : 'h-[500px]';

  return (
    <>
      {/* Chat Panel */}
      {open && (
        <div
          className={`fixed bottom-20 right-5 ${panelWidth} ${panelHeight} bg-card border border-border rounded-2xl shadow-2xl flex flex-col z-[9999] overflow-hidden transition-all duration-200`}
          data-testid="floating-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1f3b70] text-white rounded-t-2xl">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold leading-tight">NewSlate AI</h3>
                <p className="text-[10px] text-white/60">Immigration Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={startNewChat}
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                title="New conversation"
                data-testid="floating-chat-new"
              >
                <MessageSquare className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                title={expanded ? 'Minimize' : 'Expand'}
                data-testid="floating-chat-expand"
              >
                {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                data-testid="floating-chat-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="floating-chat-messages">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-2">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                  <Bot className="w-6 h-6 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">How can I help?</p>
                <p className="text-xs text-muted-foreground mb-4">Ask about immigration, cases, forms, or your CRM</p>
                <div className="grid grid-cols-1 gap-1.5 w-full max-w-[280px]">
                  {quickQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setMessage(q); inputRef.current?.focus(); }}
                      className="text-left text-xs px-3 py-2 rounded-lg border border-border hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                      data-testid={`floating-suggestion-${idx}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Bot className="w-3 h-3 text-primary" />
                    </div>
                  )}
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 ${
                    msg.role === 'user'
                      ? 'bg-[#1f3b70] text-white rounded-br-sm'
                      : 'bg-secondary text-secondary-foreground rounded-bl-sm'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <MiniMarkdown text={msg.content} />
                    ) : (
                      <p className="text-[13px] whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-6 h-6 rounded-full bg-foreground/10 flex items-center justify-center shrink-0 mt-0.5">
                      <User className="w-3 h-3 text-foreground/70" />
                    </div>
                  )}
                </div>
              ))
            )}

            {sending && (
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Bot className="w-3 h-3 text-primary" />
                </div>
                <div className="bg-secondary rounded-xl rounded-bl-sm px-3 py-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-border px-3 py-2.5">
            <form onSubmit={sendMsg} className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(e); }
                }}
                placeholder="Ask anything about immigration..."
                className="flex-1 resize-none bg-secondary border border-border rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 max-h-24 min-h-[38px]"
                rows={1}
                disabled={sending || loading}
                data-testid="floating-chat-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!message.trim() || sending || loading}
                className="rounded-xl h-[38px] w-[38px] shrink-0"
                data-testid="floating-chat-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
            <p className="text-[9px] text-muted-foreground/40 mt-1 text-center">
              AI assistant for informational purposes. Not legal advice.
            </p>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      {!open && (
        <button
          onClick={handleOpen}
          className="fixed bottom-5 right-5 w-14 h-14 rounded-full bg-[#1f3b70] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center z-[9999] group"
          data-testid="floating-chat-bubble"
        >
          <MessageSquare className="w-6 h-6 group-hover:scale-110 transition-transform" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#e68a00] rounded-full text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      )}
    </>
  );
}
